/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-unused-vars */
import {dlsIconColor} from '../theme/theme';
import {useThemeMode} from '../theme/useThemeContext';
import {Color} from '../types/types';

/**
 * Resolves the icon color without subscribing to the theme mode: the given
 * color when there is one, otherwise the DLS icon color of the active theme
 * mode. Use it from a hook which already tracks the theme mode, such as
 * `useIconDLS`, and use `useIconColor` from a component which does not.
 *
 * ```typescript
 *   import {resolveIconColor} from '@ion/dls/src/icons/useIconColor';
 *   ...
 *
 *   // the given color, otherwise the DLS icon color ...
 *   const iconColor = resolveIconColor(props.color);
 * ```
 *
 * @param color The color which is used in place of the DLS icon color.
 * @returns     The given `color`, otherwise the DLS icon color.
 */
export const resolveIconColor = (color?: Color | string): string => (
  ((color || dlsIconColor()) as unknown) as string
);

/**
 * Defines the useIconColor hook which returns the icon color and size based
 * on the provided default values, if any. The color is resolved during
 * render, and the hook subscribes to the theme mode, so the icon re-renders
 * with the color of the active theme mode when the mode changes.
 *
 * ```typescript
 *   import {useIconColor} from '@ion/dls/src/icons/useIconColor';
 *
 *   export const Icon = (props: IconProps) => {
 *     const iconColor = useIconColor(props);
 *     return (
 *       <svg width={props.size} height={props.size} viewBox='0 0 24 24' fill='none'>
 *         <path d='M10.484 18a1.313 1.313 0 100-2.625 1.313 1.313 0 000 2.625z'
 *          fill={iconColor}
 *          />
 *       </svg>
 *   };
 *
 * ```
 */
export const useIconColor = (color?: Color | string) => {
  useThemeMode();
  return resolveIconColor(color);
};
export default useIconColor;
